import { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';

import { StandardNames } from '@config/standard-names';
import { DefaultIcon, FileContentType, FileContentTypeMapping, SDate, SDuration, ServiceLocator } from '@summit/core';
import {
  DateRangeType,
  DurationFormatter,
  IUserExperience,
  MileageFormatter,
  MoneyFormatter,
  SummitFormattedText,
  Week
} from '@summit/models';

import { Router } from '@angular/router';
import { HourlyWorkType } from '@app/models/ebo/digital-logs/hourly-work-type.enum';
import { UserExperienceService } from '@app/services/user-experience.service';
import { EboObject } from '../base-classes/ebo-object';
import { Ask } from '../digital-logs/asks/ask';
import { GpsLeg } from '../digital-logs/gps/gps-leg';
import { RerouteReasonMapping } from '../digital-logs/reroute-reason.enum';
import { Settlement } from '../digital-logs/settlements/settlement';
import { Employee } from '../employee/employee';
import { Company } from '../organization/company/company';
import { ServiceArea } from '../organization/service-area/service-area';
import { PayType, PayTypeMapping } from '../payroll/shared/pay-type.enum';
import { Hub } from '../places/hub/hub';
import { Route } from '../route/route';
import { DigitalLogType, HourlyLogTypes, ReconcilableLogTypes } from '../shared/digital-log-type.enum';
import { DivisionConfiguration } from '../shared/division-configuration';
import { SpotRun } from '../spot/spot-run';
import { TruckAlias } from '../truck/truck-alias';
import { Reconcile } from './reconcile';
import { ReconcileFlags } from './reconcile-flags.enum';
import { ReconcileDriverTypes, ReconcileType } from './reconcile-type.enum';

export class ReconcileViewModel extends EboObject {
  eventDate: Dayjs;
  reconcile: Reconcile;
  pending: boolean;
  asks: Ask[];
  settlements: Settlement[];
  outsideSettlementWeek: boolean;
  gpsLegs: GpsLeg[];
  fedExID: string;
  employeeID: string;
  employee: Employee;
  logType: DigitalLogType;
  truckAliasId: string;
  truckAlias: TruckAlias;
  trailerNumbers: string;
  dollyNumbers: string;
  sealNumbers: string;
  routeID?: string;
  route?: Route;
  serviceAreaId: string;
  serviceArea: ServiceArea;
  startTime: Dayjs;
  endTime: Dayjs;
  origNum?: number;
  origHub?: Hub;
  destNum?: number;
  destHub?: Hub;
  spotRunId: string;
  spotRun: SpotRun;
  rerouted: boolean;
  run?: number;
  payTypeID: PayType;
  milesAsk?: number;
  settlementMiles?: number;
  departureDelay?: Duration;
  departureDelayApproved: boolean;
  inRouteDelayApproved: boolean;
  inRouteDelay?: Duration;
  milesGps?: number;
  delta?: number;
  milesCalc?: number;
  milesPaid?: number;
  hoursAsk: Duration;
  hoursSettlement: Duration;
  hoursDelta: Duration;
  hoursReconciled: Duration;
  fromSchedule: boolean;

  //Load Info
  packagePickupCount: number;
  packagePickupAmount: number;
  stopPickupCount: number;
  stopPickupAmount: number;
  packageDeliveredCount: number;
  packageDeliveredAmount: number;
  stopDeliveredCount: number;
  stopDeliveredAmount: number;

  //Shift Info
  get startOfShift() {
    return (this.asks?.findIndex((ask) => ask.startOfShift == true) ?? -1) >= 0;
  }
  get endOfShift() {
    return (this.asks?.findIndex((ask) => ask.endOfShift == true) ?? -1) >= 0;
  }

  //Summary items
  flags: ReconcileFlags;
  attachedContentTypes: FileContentType[];

  //cache items
  settlementGross?: number;
  hourlyWorkType: HourlyWorkType;
  duration: Duration;
  comment: string;
  rowNum?: number;
  //settlementDate: Dayjs;

  //local Caches
  private _settlementNumbers: SummitFormattedText;
  private _commentSummaryFormatted: SummitFormattedText;
  private _detailSummary: SummitFormattedText;
  private _week: Week;
  private _askCreatedOn: SummitFormattedText;

  //config
  private _showSettlementGross: boolean;

  get week(): Week {
    if (!this._week) {
      this._week = new Week(this.eventDate);
    }
    return this._week;
  }

  get reconcileType(): ReconcileType {
    return this.reconcile?.type;
  }

  static SortByTravel(inList: ReconcileViewModel[], startHub: number): ReconcileViewModel[] {
    if (inList.length <= 1) return inList;
    if (inList.length == 2) {
      if (inList[0].origNum == startHub) return inList;
      return inList.reverse();
    }
    const inputList = [...inList];
    let responseList = new Array<ReconcileViewModel>();
    let nextLegIndex = inputList.findIndex((entry) => entry.origNum == startHub);
    if (nextLegIndex < 0) nextLegIndex = 0;
    const nextLegs = inputList.splice(nextLegIndex, 1);
    responseList.push(nextLegs[0]);
    const remainingLegs = ReconcileViewModel.SortByTravel(inputList, nextLegs[0].destNum);
    responseList = responseList.concat(remainingLegs);
    return responseList;
  }

  constructor(
    jsonData?: Partial<ReconcileViewModel>,
    public router?: Router,
    private ux?: IUserExperience
  ) {
    super(jsonData);
    const uxService = ServiceLocator.injector?.get(UserExperienceService) as UserExperienceService;
    ux ??= uxService?.getUserExperience();
    this._showSettlementGross = ux?.activeEmployee?.tenant?.showSettlementGross ?? false;
    if (jsonData) {
      //console.log(`Creating based on`, jsonData);
      if (jsonData.eventDate) {
        this.eventDate = SDate.newUtc(jsonData.eventDate);
      }
      if (jsonData.employee) {
        this.employee = new Employee(jsonData.employee);
      }
      if (jsonData.route) {
        this.route = new Route(jsonData.route);
      }
      if (jsonData.startTime) this.startTime = SDate.newUtc(jsonData.startTime);
      if (jsonData.endTime) this.endTime = SDate.newUtc(jsonData.endTime);
      if (jsonData.origHub) this.origHub = new Hub(jsonData.origHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.spotRun) this.spotRun = new SpotRun(jsonData.spotRun);
      if (jsonData.serviceArea) this.serviceArea = new ServiceArea(jsonData.serviceArea);
      if (jsonData.truckAlias) this.truckAlias = new TruckAlias(jsonData.truckAlias);
      if (jsonData.asks) {
        this.asks = Ask.SortByTravel(
          jsonData.asks.map((data) => new Ask(data)),
          this.origNum
        );
      } else {
        this.asks = new Array<Ask>();
      }
      if (jsonData.settlements) {
        this.settlements = Settlement.SortByTravel(
          jsonData.settlements.map((data) => new Settlement(data)),
          this.origNum
        );
      }
      if (jsonData.departureDelay) this.departureDelay = SDuration.new(jsonData.departureDelay);
      if (jsonData.inRouteDelay) this.inRouteDelay = SDuration.new(jsonData.inRouteDelay);
      if (jsonData.hoursAsk) this.hoursAsk = SDuration.new(jsonData.hoursAsk);
      if (jsonData.hoursSettlement) this.hoursSettlement = SDuration.new(jsonData.hoursSettlement);
      if (jsonData.hoursDelta) this.hoursDelta = SDuration.new(jsonData.hoursDelta);
      if (jsonData.hoursReconciled) this.hoursReconciled = SDuration.new(jsonData.hoursReconciled);
      if (jsonData.gpsLegs) {
        this.gpsLegs = GpsLeg.SortByTravel(
          jsonData.gpsLegs.map((data) => new GpsLeg(data)),
          this.origNum
        );
      }
      //if (jsonData.settlementDate) this.settlementDate = SDate.newUtc(jsonData.settlementDate);
    }
    this.hourlyWorkType ??= HourlyWorkType.General;
    this.departureDelayApproved ??= true;
    this.inRouteDelayApproved ??= true;
    this.flags ??= ReconcileFlags.None;
    this.settlements ??= [];
    this.gpsLegs ??= [];
    this.attachedContentTypes ??= [];
    //console.log(`Creating ReconcileViewModel`, this);
    //if (this.employee?.name.startsWith('White')) console.log(`Settlement Date: ${this.settlementDate?.format('M/D')}`,jsonData);
  }

  get domicile(): Hub | null {
    return this.employee?.hub ?? null;
  }
  set domicile(value: Hub | null) {
    //NOP
  }
  get truckNum(): string {
    return this.truckAlias?.name;
  }
  set truckNum(value: string) {
    //NOP
  }

  get routeDomicile(): Hub | null {
    return this.route?.hub ?? null;
  }
  set routeDomicile(value: Hub | null) {
    //NOP
  }

  public get reconcileSplitMiles(): number {
    return this.payTypeID == PayType.Solo ? this.milesPaid : this.milesPaid / 2;
  }
  set reconcileSplitMiles(value: number) {
    //NOP
  }

  get effectiveMiles(): number {
    if (this.milesPaid != null) {
      return this.milesPaid;
    }
    return this.milesAsk ?? 0;
  }
  set effectiveMiles(value: number) {
    //NOP
  }

  get settlementNum(): string {
    if (this.settlements?.length > 0) return this.settlements[0].name;
    return undefined;
  }
  set settlementNum(value: string) {
    //NOP
  }

  get settlementDate(): Dayjs {
    if (this.settlements?.length > 0) return this.settlements[0].settlementDate;
    return this.eventDate;
  }
  set settlementDate(value: Dayjs) {
    //NOP
  }

  get settlementNumbers(): SummitFormattedText {
    if (this._settlementNumbers === undefined) {
      const response = new SummitFormattedText();
      if (
        this.reconcile &&
        this.reconcile.type === ReconcileType.Missing &&
        this.reconcile.route?.name === StandardNames.HubTransfer
      ) {
        response.AddLine(StandardNames.HubTransfer, ['bold']);
      }
      if (this.hasPending) {
        response.AddLine(`Pending =>`, ['alert-info']);
      }
      if (this.reconcile && this.reconcile.type == ReconcileType.Rejected) {
        response.AddLine('N/A');
        return response;
      }
      if (this.settlements?.length > 0) {
        this.settlements.forEach((item) => {
          if (item.name) {
            if (item.grossAmount && this._showSettlementGross) {
              response.AddLine(`${item.name} => ${MoneyFormatter(item.grossAmount)}`);
            } else {
              response.AddLine(item.name);
            }
            if (item.employeeID != this.employeeID)
              response.AddLine(`From ${item.employee?.name}'s Sett.`, ['flex', 'flex-row', 'justify-end']);
            if (item.eventDate && !item.eventDate.isSame(this.eventDate))
              response.AddLine(`From ${item.eventDate.format('ddd M/D')} Sett.`, ['flex', 'flex-row', 'justify-end']);
          }
        });
      } else if (
        this.reconcile?.type === ReconcileType.Missing &&
        this.reconcile.route?.name !== StandardNames.HubTransfer &&
        ReconcilableLogTypes.includes(this.logType)
      ) {
        response.AddLine('Missing', ['alert-warning']);
      }
      this._settlementNumbers = response;
    }
    return this._settlementNumbers;
  }

  get isTripWithGps(): boolean {
    return this.hasGps && this.hasAsk;
  }

  get payTypeSummary(): string {
    switch (this.logType) {
      case DigitalLogType.FedExSpot:
        return 'Spot';
      case DigitalLogType.Daily:
        return 'Daily';
      case DigitalLogType.TimeClock:
        return 'Time Clock';
      case DigitalLogType.Break:
        return 'Break';
      case DigitalLogType.ELD:
        return 'ELD';
      case DigitalLogType.Hourly:
        switch (this.hourlyWorkType) {
          case HourlyWorkType.General:
            break;
          default:
            return `${PayType[this.payTypeID]} - ${HourlyWorkType[this.hourlyWorkType]}`;
        }
      default:
        break;
    }
    return PayTypeMapping[this.payTypeID];
  }
  get askQuantity(): number {
    switch (this.logType) {
      case DigitalLogType.FedExPnD:
        return this.asks
          ?.map((s) => (s.packagePickupCount ?? 0) + (s.packageDeliveredCount ?? 0))
          .reduce((a, b) => a + b, 0);
      case DigitalLogType.ELD:
      case DigitalLogType.Hourly:
      case DigitalLogType.TimeClock:
      case DigitalLogType.Break:
        return this.hoursAsk?.asHours();
      default:
        return this.milesAsk;
    }
  }

  get askCreatedOn(): SummitFormattedText {
    if (this._askCreatedOn === undefined) {
      if (this.asks?.length > 0) {
        const response = new SummitFormattedText();
        if (this.asks.length > 1) {
          this.asks.forEach((trip) => {
            if (trip.createdOn)
              response.AddKeyValues(`${trip.origNum}=>${trip.destNum}`, trip.createdOn.format('M/D HH:mm'));
          });
        } else {
          if (this.asks[0].createdOn) response.AddLine(this.asks[0].createdOn.format('M/D HH:mm'));
        }
      } else {
        this._askCreatedOn = null;
      }
    }
    return this._askCreatedOn;
  }
  get askDelta(): number {
    if (!this.reconcile) return null;
    return (this.reconciledQuantity ?? 0) - (this.askQuantity ?? 0);
  }

  get settlementCompany(): Company {
    if (this.settlements.length > 0) {
      //console.log(`Settlements: `, this.settlements[0].settlementCompany);
      return this.settlements[0].settlementCompany;
    }
    return null;
  }

  private _settlementQuantity: number;
  get settlementQuantity(): number {
    if (this._settlementQuantity === undefined) {
      if (this.logType === DigitalLogType.FedExPnD) {
        return this.settlements
          .map((s) => (s.packagePickupCount ?? 0) + (s.packageDeliveredCount ?? 0))
          .reduce((a, b) => a + b, 0);
      }
      if (this.settlements.length > 0 && this.payTypeID != this.settlements[0].payTypeID) {
        if (
          (this.payTypeID == PayType.Solo || this.payTypeID == PayType.SoloTeamPay) &&
          this.settlements[0].payTypeID == PayType.Team
        ) {
          return this.settlementMiles / 2;
        }
      }
      this._settlementQuantity = HourlyLogTypes.includes(this.logType)
        ? this.hoursSettlement?.asHours()
        : this.settlementMiles;
    }
    //console.log(`settlement miles ${DigitalLogTypeMapping[this.]}`, this._settlementQuantity);
    return this._settlementQuantity;
  }

  get settlementDelta(): number {
    if (
      !this.reconcile ||
      this.logType === DigitalLogType.Hourly ||
      this.logType === DigitalLogType.ELD ||
      this.logType === DigitalLogType.Daily
    )
      return null;
    return (this.settlementQuantity ?? 0) - (this.reconciledQuantity ?? 0);
  }

  private _reconciledQuantity: number;
  get reconciledQuantity(): number {
    if (this._reconciledQuantity === undefined) {
      if (this.reconcile?.type === ReconcileType.Rejected) {
        this._reconciledQuantity = 0;
      } else if (this.logType === DigitalLogType.FedExPnD) {
        this._reconciledQuantity = (this.packagePickupCount ?? 0) + (this.packageDeliveredCount ?? 0);
      } else {
        this._reconciledQuantity = HourlyLogTypes.includes(this.logType)
          ? (this.hoursReconciled?.asHours() ?? 0)
          : (this.milesPaid ?? 0);
      }
    }
    return this._reconciledQuantity;
  }

  get hasPending(): boolean {
    if (this.isJustSettlement) return false;
    return (
      this.pending ||
      (this.asks?.filter((item) => item.pending).length ?? 0) > 0 ||
      (this.week.fedExEnd.isBefore(this.eventDate) && this.week.end.isSameOrAfter(this.eventDate))
    );
  }

  get hasReconcile(): boolean {
    return this.reconcile ? true : false;
  }

  get hasAsk(): boolean {
    return this.asks?.length > 0;
  }
  get hasSettlement(): boolean {
    return this.settlements?.length > 0;
  }

  get hasGps(): boolean {
    return this.gpsLegs?.length > 0;
  }

  get isInProgress(): boolean {
    return !this.reconcile && this.asks.length == 1 && this.asks[0].inProgress;
  }

  get isJustAsk(): boolean {
    return this.hasAsk && !(this.reconcile || this.hasGps || this.hasSettlement);
  }
  get isJustAskPossibleGPS(): boolean {
    return this.hasAsk && !(this.reconcile || this.hasSettlement);
  }

  get isJustSettlement(): boolean {
    return this.hasSettlement && !(this.reconcile || this.hasGps || this.hasAsk);
  }

  get isJustGpsLeg(): boolean {
    return this.hasGps && !(this.reconcile || this.hasAsk);
  }

  get origName(): string {
    return this.origHub?.toShortString() ?? this.origNum?.toString() ?? '--';
  }
  get origAbbr(): string {
    switch (this.logType) {
      case DigitalLogType.Break:
      case DigitalLogType.TimeClock:
        return this.startTime?.local().format('M/D hh:mm A') ?? '--';
      case DigitalLogType.FedExPnD:
      case DigitalLogType.FedExLinehaul:
      case DigitalLogType.FedExSpot:
        return this.origNum?.toString() ?? '--';
      case DigitalLogType.ELD:
      case DigitalLogType.Hourly:
        if (this.hourlyWorkType == HourlyWorkType.ELD) {
          return this.startTime?.local().format('M/D hh:mm A') ?? '--';
        } else {
          return this.origNum?.toString() ?? '--';
        }
      default:
        return '--';
    }
  }

  get destAbbr(): string {
    switch (this.logType) {
      case DigitalLogType.Break:
      case DigitalLogType.TimeClock:
        return this.isInProgress ? 'Active' : (this.endTime?.local().format('M/D hh:mm A') ?? '--');
      case DigitalLogType.FedExLinehaul:
        return this.destNum?.toString() ?? '--';
      case DigitalLogType.FedExSpot:
        return this.spotRun ? `#${this.spotRun.spotNumber}` : '--';
      case DigitalLogType.ELD:
      case DigitalLogType.Hourly:
        if (this.hourlyWorkType == HourlyWorkType.ELD) {
          return this.endTime?.local().format('M/D hh:mm A') ?? '--';
        } else {
          return '--';
        }
      case DigitalLogType.FedExPnD:
        return `${this.serviceArea?.name ?? '--'}: ${(this.stopDeliveredCount ?? 0) + (this.stopPickupCount ?? 0)} Stops`;
      default:
        return '--';
    }
  }

  get destName(): string {
    switch (this.logType) {
      case DigitalLogType.FedExLinehaul:
        //console.log('DestHub', this.destHub);
        return this.destHub?.toShortString() ?? this.destNum?.toString() ?? '--';
      case DigitalLogType.FedExSpot:
        return this.spotRun?.toShortString() ?? '--';
      default:
        return this.destAbbr;
    }
  }

  get creators(): Employee[] {
    return this.asks?.map((trip) => trip.createdBy);
  }

  isOpen(selWeek: Week): boolean {
    return (
      (this.pending && selWeek?.start.isAfter(this.eventDate) && !this.hasReconcile) ||
      (selWeek?.fedExEnd.isSameOrAfter(this.eventDate) && !(this.hasReconcile || this.pending || this.isJustGpsLeg)) //current week
    );
  }

  get commentSummary(): string {
    const response = [];
    if (this.hourlyWorkType !== HourlyWorkType.General) response.push(`${HourlyWorkType[this.hourlyWorkType]}`);
    if (this.asks?.length > 0) {
      this.asks.forEach((trip) => {
        //console.log('Processing trip', trip);
        if (this.asks.length == 1) {
          if (trip.liveLoad) response.push('Live Load');
          if (trip.departureDelay) {
            if (this.departureDelayApproved && !this.departureDelay)
              response.push(`Departure Delay: ${DurationFormatter(trip.departureDelay)}`);
            else response.push(`Departure Delay Rejected: (${DurationFormatter(trip.departureDelay)})`);
          }
          if (trip.inRouteDelay) {
            if (this.inRouteDelayApproved) response.push(`In Route Delay: ${DurationFormatter(trip.inRouteDelay)}`);
            else response.push(`In Route Delay Rejected: (${DurationFormatter(trip.inRouteDelay)})`);
          }
          if (trip.comment) response.push(trip.comment);
        } else {
          if (trip.liveLoad) response.push(`${trip.destAbbr}: Live Load`);
          if (trip.departureDelay) {
            if (this.departureDelayApproved)
              response.push(
                `${trip.origNum}=> ${trip.destAbbr}: Departure Delay: ${DurationFormatter(trip.departureDelay)}`
              );
            else
              response.push(
                `${trip.origNum}=> ${trip.destAbbr}: Departure Delay Rejected: (${DurationFormatter(
                  trip.departureDelay
                )})`
              );
          }
          if (trip.inRouteDelay) {
            if (this.inRouteDelayApproved)
              response.push(
                `${trip.origNum}=> ${trip.destAbbr}: In Route Delay: ${DurationFormatter(trip.inRouteDelay)}`
              );
            else
              response.push(
                `${trip.origNum}=> ${trip.destAbbr}: In Route Delay Rejected: (${DurationFormatter(trip.inRouteDelay)})`
              );
          }
          if (trip.comment) response.push(`${trip.origNum}=> ${trip.destAbbr}: ${trip.comment}`);
        }
      });
    }
    if (this.comment) response.push(this.comment);
    return response.join(' \n');
  }
  get commentSummaryFormatted(): SummitFormattedText {
    if (this._commentSummaryFormatted === undefined) {
      const response = new SummitFormattedText();
      const icons = [];
      if (this.fromSchedule) icons.push(DefaultIcon.Schedule);
      if (this.startOfShift) icons.push(DefaultIcon.Start);
      if (this.endOfShift) icons.push(DefaultIcon.End);
      if (icons.length > 0) response.AddIcons(icons);
      if (this.attachedContentTypes.length > 0) response.AddIcons([DefaultIcon.Attach]);
      if (this.isInProgress)
        response.AddLine(`In Progress`, ['flex', 'flex-row', 'justify-center', 'text-bold', 'alert-ebo']);
      if (this.hourlyWorkType !== HourlyWorkType.General)
        response.AddLine(`${HourlyWorkType[this.hourlyWorkType]}`, ['flex', 'flex-row', 'justify-center', 'text-bold']);
      if (this.departureDelay && this.reconcile)
        response.AddKeyValues(`Dep. Delay (Override)`, DurationFormatter(this.departureDelay));

      if (this.hasGps) {
        response.AddIcons([DefaultIcon.GPS]);
      }
      if (this.asks?.length > 0) {
        this.asks.forEach((trip) => {
          //console.log('Processing trip', trip);
          const commentPrefix = this.asks.length > 1 ? `${trip.origNum}=>${trip.destNum}: ` : '';
          if (trip.departureDelay) {
            if (this.departureDelayApproved)
              response.AddKeyValues(`${commentPrefix}Dep. Delay`, DurationFormatter(trip.departureDelay));
            else {
              response.AddKeyValues(`${commentPrefix}Dep. Delay`, DurationFormatter(trip.departureDelay), [
                'line-through'
              ]);
              response.AddLine(`Dep. Delay Rejected`);
            }
          }
          if (trip.rerouted) {
            const rerouteStyle =
              this.reconcile && ReconcileDriverTypes.indexOf(this.reconcile.type) < 0 ? ['line-through'] : [];
            response.AddKeyValues(`${commentPrefix}Re-Routed`, RerouteReasonMapping[trip.rerouteReason], rerouteStyle);
            if (this.milesAsk != this.settlementMiles)
              response.AddKeyValues(`${commentPrefix}Re-Routed Mi.`, trip.miles, rerouteStyle);
            if (rerouteStyle.length > 0) response.AddLine(`Re-Route Rejected`);
          }
          if (trip.inRouteDelay) {
            if (this.inRouteDelayApproved)
              response.AddKeyValues(`${commentPrefix}In Route Delay`, DurationFormatter(trip.inRouteDelay));
            else {
              response.AddKeyValues(`${commentPrefix}In Route Delay`, DurationFormatter(trip.inRouteDelay), [
                'line-through'
              ]);
              response.AddLine(`In Route Delay Rejected`);
            }
          }
          let valueClass = [];
          if (trip.comment === 'Miles Estimated') valueClass.push('alert-warning');
          //if (trip.comment === 'Settlement Jump') valueClass.push('alert-warning');
          if (valueClass.length === 0) valueClass = null;
          if (trip.comment) {
            if (this.asks.length == 1) response.AddLine(trip.comment, valueClass);
            else response.AddKeyValues(`${trip.origNum}=> ${trip.destNum}`, trip.comment, valueClass);
          }
        });
      }
      const reconcileValueClass = [];
      //Add Reconcile Comment
      if ((this.flags & ReconcileFlags.SettlementJump) > 0) reconcileValueClass.push('alert-warning');
      if (this.comment) response.AddLine(this.comment, reconcileValueClass);
      if (
        this.logType != DigitalLogType.FedExPnD &&
        this.milesCalc != this.milesPaid &&
        this.settlementMiles != this.milesPaid &&
        this.milesPaid !== 0
      ) {
        if (this.comment === 'Partial Settlement Jump') {
          response.AddKeyValues('Mileage Est.', MileageFormatter(this.milesCalc));
          response.AddKeyValues('Adjusted from', MileageFormatter(this.settlementMiles));
        } else {
          response.AddKeyValues('Mileage Est.', MileageFormatter(this.milesCalc));
        }
      }
      this._commentSummaryFormatted = response;
    }
    return this._commentSummaryFormatted;
  }

  get detailSummary(): SummitFormattedText {
    if (this._detailSummary === undefined) {
      const response = new SummitFormattedText();
      this.asks.forEach((log) => {
        if (log.startTime)
          response.AddLine(`Started: ${log.startTime.local().format('M/D HH:mm')}`, [
            'flex',
            'flex-row',
            'justify-center',
            'text-bold'
          ]);
        if (log.odometerBegin) response.AddKeyValues(`Odometer`, MileageFormatter(log.odometerBegin));
        if (log.eldIssue)
          response.AddLine(`ELD issue`, ['flex', 'flex-row', 'justify-center', 'text-bold', 'alert-warning']);
        if (log.eldIssueComment) response.AddHtml(log.eldIssueComment);
        if (log.paperLogsRequired)
          response.AddLine(`Paper Logs Required`, ['flex', 'flex-row', 'justify-center', 'text-bold', 'alert-warning']);
        if (log.attachedContentTypes.includes(FileContentType.HosLog))
          response.AddLine(
            FileContentTypeMapping[FileContentType.HosLog],
            ['text-bold', 'text-green-500'],
            null,
            DefaultIcon.CheckBox
          );
        if (log.trailerCount)
          response.AddLine(`${log.trailerCount} Trailers`, ['flex', 'flex-row', 'justify-center', 'text-bold']);
        if (log.trailer1 && log.trailer1Seal) response.AddKeyValues(`#1`, `${log.trailer1} ${log.trailer1Seal}`);
        else if (log.trailer1 || log.trailer1Seal) response.AddKeyValues(`#1`, `${log.trailer1 ?? log.trailer1Seal}`);
        if (log.trailerCount > 0) {
          if (log.attachedContentTypes.includes(FileContentType.Trailer1Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer1Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(FileContentType.Trailer1Connections)
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer1Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }
        if (log.trailer2Dolly) response.AddKeyValues(`#2 Dolly`, `${log.trailer2Dolly}`);
        if (log.trailer2 && log.trailer2Seal) response.AddKeyValues(`#2`, `${log.trailer2} ${log.trailer2Seal}`);
        else if (log.trailer2 || log.trailer2Seal) response.AddKeyValues(`#2`, `${log.trailer2 ?? log.trailer2Seal}`);
        if (log.trailerCount > 1) {
          if (log.attachedContentTypes.includes(FileContentType.Trailer2Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer2Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(FileContentType.Trailer2Connections)
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer2Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }
        if (log.trailer3Dolly) response.AddKeyValues(`#3 Dolly`, `${log.trailer3Dolly}`);
        if (log.trailer3 && log.trailer3Seal) response.AddKeyValues(`#3`, `${log.trailer3} ${log.trailer3Seal}`);
        else if (log.trailer3 || log.trailer3Seal) response.AddKeyValues(`#3`, `${log.trailer3 ?? log.trailer3Seal}`);
        if (log.trailerCount > 2) {
          if (log.attachedContentTypes.includes(FileContentType.Trailer3Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer3Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(FileContentType.Trailer3Connections)
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.Trailer3Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }
        if (log.departureTime)
          response.AddLine(`Departed: ${log.departureTime.local().format('M/D HH:mm')}`, [
            'flex',
            'flex-row',
            'justify-center',
            'text-bold'
          ]);
        if (log.departureDelay) response.AddKeyValues(`Departure Delay`, `${DurationFormatter(log.departureDelay)}`);
        if (log.returnTrailerCount)
          response.AddLine(`${log.trailerCount} Trailers`, ['flex', 'flex-row', 'justify-center', 'text-bold']);
        if (log.returnTrailer1 && log.returnTrailer1Seal)
          response.AddKeyValues(`#1`, `${log.returnTrailer1} ${log.returnTrailer1Seal}`);
        else if (log.returnTrailer1 || log.returnTrailer1Seal)
          response.AddKeyValues(`#1`, `${log.returnTrailer1 ?? log.returnTrailer1Seal}`);
        if (log.returnTrailerCount > 0) {
          if (log.attachedContentTypes.includes(FileContentType.ReturnTrailer1Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer1Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(
              FileContentType.ReturnTrailer1Connections
            )
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer1Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }
        if (log.returnTrailer2Dolly) response.AddKeyValues(`#2 Dolly`, `${log.returnTrailer2Dolly}`);
        if (log.returnTrailer2 && log.returnTrailer2Seal)
          response.AddKeyValues(`#2`, `${log.returnTrailer2} ${log.returnTrailer2Seal}`);
        else if (log.returnTrailer2 || log.returnTrailer2Seal)
          response.AddKeyValues(`#2`, `${log.returnTrailer2 ?? log.returnTrailer2Seal}`);
        if (log.returnTrailerCount > 1) {
          if (log.attachedContentTypes.includes(FileContentType.ReturnTrailer2Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer2Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(
              FileContentType.ReturnTrailer1Connections
            )
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer2Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }

        if (log.returnTrailer3Dolly) response.AddKeyValues(`#3 Dolly`, `${log.returnTrailer3Dolly}`);
        if (log.returnTrailer3 && log.returnTrailer3Seal)
          response.AddKeyValues(`#3`, `${log.returnTrailer3} ${log.returnTrailer3Seal}`);
        else if (log.returnTrailer3 || log.returnTrailer3Seal)
          response.AddKeyValues(`#3`, `${log.returnTrailer3 ?? log.returnTrailer3Seal}`);
        if (log.returnTrailerCount > 2) {
          if (log.attachedContentTypes.includes(FileContentType.ReturnTrailer3Connections))
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer3Connections],
              ['text-bold', 'text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else if (
            (
              this.ux?.divisionConfig as DivisionConfiguration
            )?.reportParameters.digitalLogParameters.fileContentTypes.includes(
              FileContentType.ReturnTrailer1Connections
            )
          )
            response.AddLine(
              FileContentTypeMapping[FileContentType.ReturnTrailer3Connections],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        }
        if (log.arrivalTime)
          response.AddLine(`Arrival: ${log.arrivalTime.local().format('M/D HH:mm')}`, [
            'flex',
            'flex-row',
            'justify-center',
            'text-bold'
          ]);
        if (log.odometerEnd) response.AddKeyValues(`Odometer End`, MileageFormatter(log.odometerEnd));
        if (log.rerouted)
          response.AddLine(`Rerouted: ${RerouteReasonMapping[log.rerouteReason]}`, [
            'flex',
            'flex-row',
            'justify-center',
            'text-bold'
          ]);
        if (log.inRouteDelay) response.AddKeyValues(`In Route Delay`, `${DurationFormatter(log.inRouteDelay)}`);
        if (log.rerouted) response.AddKeyValues(`Miles`, `${MileageFormatter(log.miles)}`);
        if (log.endTime)
          response.AddLine(`Finished: ${log.endTime.local().format('M/D HH:mm')}`, [
            'flex',
            'flex-row',
            'justify-center',
            'text-bold'
          ]);
      });
      this._detailSummary = response;
    }
    return this._detailSummary;
  }
  set detailSummary(value: SummitFormattedText) {
    //NOP
  }

  private _teamSummary: SummitFormattedText;
  get teamSummary(): SummitFormattedText {
    if (this._teamSummary === undefined) {
      this._teamSummary = new SummitFormattedText();
      this._teamSummary.AddLine(this.payTypeSummary);
      if (this.hasCoDriver()) {
        const selWeek =
          this.settlements?.length > 0
            ? new Week(this.settlements[0].eventDate, DateRangeType.SettlementWeek)
            : new Week(this.eventDate);
        const dateRange = selWeek.ToDateRange(DateRangeType.TenantWeek);
        if (this.settlements?.length > 0) {
          this._teamSummary.AddLine(this.settlements[0]?.fedExIDTeammate, [], {
            path: ['/reconcile'],
            queryParams: {
              WeekRange: JSON.stringify({ filterData: dateRange }),
              fedExID: this.settlements[0]?.fedExIDTeammate?.toString(),
              tab: 'Reconcile'
            },
            target: '_blank'
          });
        } else if (this.asks?.length > 0) {
          var coDriver = this.asks.find((ask) => ask.coDriver)?.coDriver;
          if (coDriver) {
            this._teamSummary.AddLine(coDriver?.name, [], {
              path: ['/reconcile'],
              queryParams: {
                WeekRange: JSON.stringify({ filterData: dateRange }),
                filter: coDriver.name,
                tab: 'Reconcile'
              },
              target: '_blank'
            });
          }
        }
      }
    }
    return this._teamSummary;
  }

  get effectiveOdometer(): number {
    return this.asks && this.asks.length >= 1
      ? this.asks.map((m) => m.odometerEnd ?? m.odometerBegin).reduce((a, b) => (a < b ? a : b))
      : undefined;
  }

  isEditable(ux: IUserExperience) {
    return !this.reconcile && (ux.isBackOffice() || new Week(this.eventDate).isEditable(ux));
  }
  isDeletable(ux: IUserExperience) {
    const locked = this.reconcile || this.pending || this.isJustSettlement;
    if (locked) return false;
    return this.isEditable(ux);
  }

  toShortString(format: string = null): string {
    let response = '';
    if (format == 'TripComment') {
      if (this.comment) {
        response = `${this.eventDate ? this.eventDate.format('M/D') : 'Unknown'} ${this.origNum}=>${this.destNum}: ${
          this.comment
        }`;
      }
    } else {
      response = this.eventDate ? this.eventDate.format('M/D/YY') : 'Unknown';
      if (this.run) {
        response += `R${this.run}`;
      }
      response += `- ${this.employee ? this.employee.fedExID : 'Unknown'} - ${this.origNum}->${this.destNum} = ${
        this.milesPaid ?? this.milesAsk ?? 0
      }`;
    }
    return response;
  }

  getNextLeg(): Partial<Ask> {
    const newTrip = new Object() as Partial<Ask>;
    newTrip.logType = this.logType;
    newTrip.eventDate = SDate.new(this.eventDate);
    newTrip.employeeID = this.employeeID;
    newTrip.truckAliasId = this.truckAliasId;
    if (this.logType === DigitalLogType.FedExLinehaul) {
      newTrip.origNum = this.destNum;
      newTrip.destNum = this.origNum;
    } else {
      newTrip.origNum = this.origNum;
    }
    newTrip.miles = undefined;
    newTrip.payTypeID = this.payTypeID;
    newTrip.routeID = this.routeID;
    if (this.asks?.length > 0) {
      const maxOdometer: number = this.asks.reduce(
        (max: number, trip) => (trip.odometerEnd > max ? trip.odometerEnd : max),
        0
      );
      if (maxOdometer) newTrip.odometerBegin = maxOdometer;
    }
    return newTrip;
  }

  toSettlementIds(): string {
    return this.settlements.map((data) => data.name).join(',');
  }

  isDestAskChanged(): boolean {
    //console.log('Checking dest changes', this.asks[this.asks.length - 1].destAbbr, this.destAbbr);
    return this.asks && this.asks.length > 0 && this.asks[this.asks.length - 1].destAbbr !== this.destAbbr;
  }
  getDestAskCorrections(): string {
    return this.asks.map((data) => data.destAbbr).join('=>');
  }

  isDestSettlementChanged(): boolean {
    return (
      this.settlements &&
      this.settlements.length > 0 &&
      this.settlements[this.settlements.length - 1].destAbbr !== this.destAbbr
    );
  }
  getDestSettlementCorrections(): string {
    return this.settlements.map((data) => data.destNum).join('=>');
  }

  // Distinct values = .filter((x, i, a) => a.indexOf(x) == i)
  getDestString(): string {
    if (this.reconcile && (this.reconcile.type === ReconcileType.Driver || !this.settlements)) {
      //console.log('using trips', this);
      return this.asks
        .map((data) => data.destAbbr)
        .filter((x, i, a) => a.indexOf(x) === i)
        .join('=>');
    } else if (this.settlements && this.settlements.length > 1) {
      //console.log('using settlements', this);
      return this.settlements
        .map((data) => data.destAbbr)
        .filter((x, i, a) => a.indexOf(x) === i)
        .join('=>');
    }
    return this.destAbbr?.toString().trim();
  }

  isOrigAskChanged(): boolean {
    return this.asks && this.asks.length >= 1 && this.asks[0].origNum !== this.origNum;
  }
  isOrigSettlementChanged(): boolean {
    return this.settlements && this.settlements.length >= 1 && this.settlements[0].origNum !== this.origNum;
  }
  getOrigAskCorrections(): string {
    return this.asks
      .map((data) => data.origNum)
      .filter((x, i, a) => a.indexOf(x) === i)
      .join(',');
  }
  getOrigSettlementCorrections(): string {
    return this.settlements
      .map((data) => data.origNum)
      .filter((x, i, a) => a.indexOf(x) === i)
      .join(',');
  }

  hasCoDriver(): boolean {
    if (this.payTypeID == PayType.Team) return true;
    return (
      (this.asks?.length > 0 && this.asks.some((ask) => ask.coDriverId)) ||
      (this.settlements?.length > 0 && this.settlements.some((settlement) => settlement.fedExIDTeammate))
    );
  }
}
